import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { X, Search, Pencil, ShieldCheck, ShieldAlert, HeartPulse, Award, UserPlus, Users, Download, Upload, CheckCircle2, AlertTriangle, Plus } from "lucide-react";
import { StorageImage } from "@/components/app/ImageUpload";
import { differenceInCalendarDays, parseISO, format } from "date-fns";
import { DismissibleBanner } from "@/components/app/DismissibleBanner";
import { BulkQualificationDialog } from "@/components/coaches/BulkQualificationDialog";
import { QualificationImportDialog } from "@/components/coaches/QualificationImportDialog";
import { ProposeCoachDialog } from "@/components/proposals/ProposeCoachDialog";
import { MyCoachProposals } from "@/components/proposals/MyCoachProposals";

const AGE_ORDER = ["U7","U8","U9","U10","U11","U12","U13","U14","U15","U16","U18","Adults"];
const ROLE_TYPES = ["Head Coach", "Assistant Coach", "Team Manager", "Goalkeeper Coach", "Volunteer"];

const emptyProfile = {
  role_type: "",
  highest_coaching_qualification: "",
  dbs_held: false,
  dbs_expiry: "",
  safeguarding_children_held: false,
  safeguarding_children_expiry: "",
  intro_first_aid_held: false,
  intro_first_aid_expiry: "",
  compliance_reminders_enabled: true,
};

const emptyPromote = { email: "", phone: "", send_welcome_email: true, force_password_change: true };

function expiryStatus(dateStr) {
  if (!dateStr) return null;
  try {
    const days = differenceInCalendarDays(parseISO(dateStr), new Date());
    if (days < 0) return { label: "Expired", tone: "bg-red-500/20 text-red-500 border-red-500/40" };
    if (days <= 60) return { label: `Expires in ${days}d`, tone: "bg-amber-500/20 text-amber-500 border-amber-500/40" };
    return { label: `Valid to ${format(parseISO(dateStr), "d MMM yyyy")}`, tone: "bg-[#83B68B]/20 text-[#83B68B] border-[#83B68B]/30" };
  } catch { return null; }
}

function CertRow({ icon: Icon, label, held, expiry }) {
  const status = held ? expiryStatus(expiry) : { label: "Not held", tone: "bg-muted opacity-70 border-border" };
  return (
    <div className="flex items-center gap-2 text-xs">
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${held ? "text-[#83B68B]" : "opacity-40"}`} />
      <span className="opacity-70 flex-shrink-0">{label}</span>
      {status && <span className={`text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded border ${status.tone}`}>{status.label}</span>}
    </div>
  );
}

export default function Coaches() {
  const { user } = useAuth();
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAge, setFilterAge] = useState("all");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [profileForm, setProfileForm] = useState(emptyProfile);
  const [saving, setSaving] = useState(false);
  const [promoting, setPromoting] = useState(null); // team-only coach object being promoted
  const [promoteForm, setPromoteForm] = useState(emptyPromote);
  const [promoteResult, setPromoteResult] = useState(null); // { temp_password, teams_linked, welcome_email_sent }
  const [promoteBusy, setPromoteBusy] = useState(false);
  const [proposeOpen, setProposeOpen] = useState(false);
  const [editingProposal, setEditingProposal] = useState(null);
  const [proposalsKey, setProposalsKey] = useState(0);

  // ---- Bulk convert coach → user ----
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const bulkFileRef = useRef(null);
  const resetBulk = () => {
    setBulkFile(null); setBulkPreview(null); setBulkResult(null); setBulkBusy(false);
    if (bulkFileRef.current) bulkFileRef.current.value = "";
  };
  const teamOnlyCount = useMemo(() => coaches.filter(c => c.source === "team").length, [coaches]);

  const downloadBulkTemplate = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/coaches/bulk-template`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("template download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "elsenham-coach-logins-template.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Template downloaded — one row per coach without a login`);
    } catch { toast.error("Couldn't download the template"); }
  };

  const previewBulk = async (file) => {
    setBulkBusy(true); setBulkPreview(null); setBulkResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/coaches/bulk-promote-preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkPreview(data);
      if (data.valid_count === 0) toast.error("No valid rows — fix the errors and re-upload");
      else toast.success(`${data.valid_count} coach login${data.valid_count === 1 ? "" : "s"} ready`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't read that CSV");
    } finally { setBulkBusy(false); }
  };

  const commitBulk = async () => {
    if (!bulkFile) return;
    setBulkBusy(true);
    try {
      const fd = new FormData(); fd.append("file", bulkFile);
      const { data } = await api.post("/admin/coaches/bulk-promote-commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkResult(data);
      toast.success(`Created ${data.created_count} coach login${data.created_count === 1 ? "" : "s"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally { setBulkBusy(false); }
  };

  const isAdmin = user?.role === "admin";

  const load = async () => {
    try { const { data } = await api.get("/coaches"); setCoaches(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // ---- Bulk qualification update (admin) ----
  const [picked, setPicked] = useState(new Set());
  const [bulkQualOpen, setBulkQualOpen] = useState(false);
  const [qualImportOpen, setQualImportOpen] = useState(false);
  const togglePick = (id) => setPicked(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const uniqueAges = useMemo(() => {
    const s = new Set();
    coaches.forEach(c => (c.teams || []).forEach(t => t.age_group && s.add(t.age_group)));
    return AGE_ORDER.filter(a => s.has(a));
  }, [coaches]);

  const filteredCoaches = useMemo(() => coaches.filter(c => {
    if (filterAge !== "all" && !(c.teams || []).some(t => t.age_group === filterAge)) return false;
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      const hay = [c.name, c.email, c.phone, c.role_type, c.highest_coaching_qualification, ...(c.teams || []).flatMap(t => [t.name, t.age_group])].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  }), [coaches, filterAge, q]);

  const anyFilter = filterAge !== "all" || q.trim() !== "";
  const clearFilters = () => { setFilterAge("all"); setQ(""); };
  const initials = (n) => (n || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  const openEdit = (c) => {
    setEditing(c);
    setProfileForm({
      role_type: c.role_type || "",
      highest_coaching_qualification: c.highest_coaching_qualification || "",
      dbs_held: !!c.dbs_held,
      dbs_expiry: c.dbs_expiry || "",
      safeguarding_children_held: !!c.safeguarding_children_held,
      safeguarding_children_expiry: c.safeguarding_children_expiry || "",
      intro_first_aid_held: !!c.intro_first_aid_held,
      intro_first_aid_expiry: c.intro_first_aid_expiry || "",
      compliance_reminders_enabled: c.compliance_reminders_enabled !== false,
    });
  };
  const saveProfile = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.patch(`/admin/coaches/${editing.id}`, {
        role_type: profileForm.role_type || null,
        highest_coaching_qualification: profileForm.highest_coaching_qualification || null,
        dbs_held: !!profileForm.dbs_held,
        dbs_expiry: profileForm.dbs_expiry || null,
        safeguarding_children_held: !!profileForm.safeguarding_children_held,
        safeguarding_children_expiry: profileForm.safeguarding_children_expiry || null,
        intro_first_aid_held: !!profileForm.intro_first_aid_held,
        intro_first_aid_expiry: profileForm.intro_first_aid_expiry || null,
        compliance_reminders_enabled: !!profileForm.compliance_reminders_enabled,
      });
      toast.success("Coach profile updated");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const openPromote = (c) => { setPromoting(c); setPromoteForm(emptyPromote); setPromoteResult(null); };
  const submitPromote = async () => {
    if (!promoting || !promoteForm.email) return toast.error("Email required");
    setPromoteBusy(true);
    try {
      const { data } = await api.post("/admin/coaches/promote", {
        coach_name: promoting.name,
        email: promoteForm.email,
        phone: promoteForm.phone || null,
        send_welcome_email: !!promoteForm.send_welcome_email,
        force_password_change: !!promoteForm.force_password_change,
      });
      setPromoteResult(data);
      toast.success(`${data.teams_linked} team${data.teams_linked === 1 ? "" : "s"} linked to new account`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPromoteBusy(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Directory</div>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-2">Coaches</h1>
          <p className="opacity-60 mt-2 text-sm">{coaches.length} coach{coaches.length !== 1 && "es"} · tap a phone number or email to get in touch. {isAdmin ? "Click the pencil to update DBS / safeguarding / first aid." : "Admin only can edit."}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setProposeOpen(true)}
            data-testid="propose-coach-btn"
            className="px-5 py-2.5 rounded-md border border-[#83B68B]/50 text-[#83B68B] hover:bg-[#83B68B]/10 inline-flex items-center gap-2 text-xs tracking-widest uppercase font-display font-bold"
          >
            <Plus className="w-4 h-4" /> Register new coach
          </button>
          {isAdmin && <Link to="/app/admin" className="btn-volt px-5 py-2.5 rounded-md text-sm">Edit in Admin</Link>}
        </div>
      </div>

      {isAdmin && (
        <div className="mb-4 flex flex-wrap items-center gap-3 text-xs card-tactical rounded-md p-3" data-testid="bulk-qual-bar">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="bulk-qual-select-all" className="accent-[#83B68B]"
              checked={filteredCoaches.filter(c => c.source !== "team").length > 0 && filteredCoaches.filter(c => c.source !== "team").every(c => picked.has(c.id))}
              onChange={e => setPicked(e.target.checked ? new Set(filteredCoaches.filter(c => c.source !== "team").map(c => c.id)) : new Set())} />
            Select all shown ({filteredCoaches.filter(c => c.source !== "team").length})
          </label>
          <span className="opacity-60">{picked.size} selected</span>
          <button type="button" disabled={!picked.size} onClick={() => setBulkQualOpen(true)} data-testid="bulk-qual-open" className="btn-volt px-4 py-2 rounded text-[10px] tracking-widest uppercase disabled:opacity-40 inline-flex items-center gap-1.5"><Award className="w-3 h-3" /> Bulk update qualifications</button>
          {picked.size > 0 && <button type="button" onClick={() => setPicked(new Set())} className="underline opacity-70 hover:opacity-100">Clear</button>}
          <button type="button" onClick={() => setQualImportOpen(true)} data-testid="qual-import-open" className="ml-auto px-4 py-2 rounded border border-[#83B68B]/50 text-[#83B68B] text-[10px] tracking-widest uppercase inline-flex items-center gap-1.5 hover:bg-[#83B68B]/10"><Upload className="w-3 h-3" /> Import FA export (CSV)</button>
        </div>
      )}
      <QualificationImportDialog open={qualImportOpen} onClose={() => setQualImportOpen(false)} onDone={load} />
      <BulkQualificationDialog open={bulkQualOpen} onClose={() => setBulkQualOpen(false)} coachIds={[...picked]} onDone={() => { setPicked(new Set()); load(); }} />

      <ProposeCoachDialog open={proposeOpen} editing={editingProposal} onClose={() => { setProposeOpen(false); setEditingProposal(null); }} onSubmitted={() => setProposalsKey(k => k + 1)} />
      <MyCoachProposals refreshKey={proposalsKey} onEdit={p => { setEditingProposal(p); setProposeOpen(true); }} />

      {isAdmin && teamOnlyCount > 0 && (
        <DismissibleBanner id={`bulk-convert-${teamOnlyCount}`} className="mb-6">
        <div className="rounded-md border border-[#83B68B]/40 bg-[#83B68B]/5 p-5 flex flex-wrap gap-4 items-center justify-between" data-testid="bulk-convert-banner">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded bg-[#83B68B]/20 border border-[#83B68B]/40 flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-[#83B68B]" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Convert coach → user</div>
              <div className="font-display font-black text-lg">
                {teamOnlyCount} coach{teamOnlyCount === 1 ? "" : "es"} without a login
              </div>
              <div className="text-xs opacity-70 mt-0.5">Download the pre-filled CSV, add each email, upload it back — every team stays linked to its coach.</div>
            </div>
          </div>
          <button
            onClick={() => { resetBulk(); setBulkOpen(true); }}
            data-testid="bulk-convert-open-btn"
            className="btn-volt px-5 py-2.5 rounded-md text-sm inline-flex items-center gap-2 flex-shrink-0"
          >
            <Upload className="w-4 h-4" /> Bulk create logins
          </button>
        </div>
        </DismissibleBanner>
      )}

      {loading ? (
        <div className="opacity-50 font-display tracking-widest">LOADING…</div>
      ) : coaches.length === 0 ? (
        <div className="card-tactical rounded-md p-10 text-center opacity-60">No coaches added yet.</div>
      ) : (
        <>
          <div className="mb-6 flex flex-wrap gap-3 items-center card-tactical rounded-md p-4" data-testid="coaches-filter-bar">
            <div className="text-[10px] tracking-widest uppercase opacity-60 mr-1">Filter</div>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 opacity-40" />
              <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Name, team, email…" className="pl-9 w-64" data-testid="coaches-filter-search" />
            </div>
            <Select value={filterAge} onValueChange={setFilterAge}>
              <SelectTrigger className="w-44" data-testid="coaches-filter-age"><SelectValue placeholder="Age group" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All age groups</SelectItem>
                {uniqueAges.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
            {anyFilter && (
              <button onClick={clearFilters} className="text-xs tracking-widest uppercase px-3 py-1.5 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded inline-flex items-center gap-1.5" data-testid="coaches-filter-clear">
                <X className="w-3 h-3" /> Clear
              </button>
            )}
            <div className="flex-1" />
            <div className="text-xs opacity-60" data-testid="coaches-filter-count">{filteredCoaches.length} of {coaches.length}</div>
          </div>

          {filteredCoaches.length === 0 ? (
            <div className="card-tactical rounded-md p-10 text-center opacity-60">No coaches match those filters.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4" data-testid="coaches-list">
              {filteredCoaches.map(c => (
                <div key={c.id} className={`card-tactical rounded-md p-5 ${picked.has(c.id) ? "border-[#83B68B]/60" : ""}`} data-testid={`coach-card-${c.id}`}>
                  <div className="flex gap-4 items-start">
                    {isAdmin && c.source !== "team" && (
                      <input type="checkbox" checked={picked.has(c.id)} onChange={() => togglePick(c.id)} data-testid={`coach-pick-${c.id}`} className="mt-5 accent-[#83B68B]" />
                    )}
                    {c.avatar_path ? (
                      <StorageImage path={c.avatar_path} alt={c.name} className="w-14 h-14 rounded-full object-cover flex-shrink-0 border border-border" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center text-sm font-display font-black flex-shrink-0 border border-border">{initials(c.name)}</div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2 flex-wrap">
                        <div className="font-display font-bold text-lg tracking-tight">{c.name}</div>
                        {c.role_type && <span className="text-[9px] tracking-widest uppercase text-[#83B68B]">{c.role_type}</span>}
                      </div>
                      <div className="mt-2 text-xs space-y-1">
                        {c.email && (
                          <div><a href={`mailto:${c.email}`} data-testid={`coach-email-${c.id}`} className="opacity-80 hover:text-[#83B68B] hover:underline break-all">✉ {c.email}</a></div>
                        )}
                        {c.phone && (
                          <div><a href={`tel:${c.phone.replace(/\s+/g, "")}`} data-testid={`coach-phone-${c.id}`} className="opacity-80 hover:text-[#83B68B] hover:underline">📞 {c.phone}</a></div>
                        )}
                        {!c.email && !c.phone && <div className="opacity-40">No contact details on file</div>}
                      </div>
                    </div>
                    {isAdmin && c.source !== "team" && (
                      <button onClick={()=>openEdit(c)} data-testid={`coach-edit-${c.id}`} className="opacity-60 hover:opacity-100 hover:text-[#83B68B] p-2 flex-shrink-0" title="Edit coaching profile">
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {c.source === "team" && (
                    <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                      <div className="text-[10px] tracking-widest uppercase opacity-50" data-testid={`coach-team-only-${c.id}`}>Not a registered user · listed on team records only</div>
                      {isAdmin && (
                        <button onClick={()=>openPromote(c)} data-testid={`coach-promote-${c.id}`} className="text-[10px] tracking-widest uppercase px-3 py-1.5 border border-[#83B68B]/40 hover:border-[#83B68B] hover:text-[#83B68B] rounded inline-flex items-center gap-1.5">
                          <UserPlus className="w-3 h-3" /> Create account
                        </button>
                      )}
                    </div>
                  )}

                  {isAdmin && c.source !== "team" && (
                  <div className="mt-4 border-t border-border pt-3 space-y-1.5" data-testid={`coach-certs-${c.id}`}>
                    <CertRow icon={ShieldCheck} label="DBS" held={c.dbs_held} expiry={c.dbs_expiry} />
                    <CertRow icon={ShieldAlert} label="Safeguarding Children" held={c.safeguarding_children_held} expiry={c.safeguarding_children_expiry} />
                    <CertRow icon={HeartPulse} label="Intro to First Aid" held={c.intro_first_aid_held} expiry={c.intro_first_aid_expiry} />
                    {c.highest_coaching_qualification && (
                      <div className="flex items-center gap-2 text-xs pt-1">
                        <Award className="w-3.5 h-3.5 text-[#83B68B] flex-shrink-0" />
                        <span className="opacity-70 flex-shrink-0">Qualification</span>
                        <span className="opacity-90">{c.highest_coaching_qualification}</span>
                      </div>
                    )}
                  </div>
                  )}

                  {(c.teams || []).length > 0 && (
                    <div className="mt-4 border-t border-border pt-3">
                      <div className="text-[10px] tracking-widest uppercase opacity-50 mb-2">Teams</div>
                      <div className="flex flex-wrap gap-1.5">
                        {c.teams.map(t => (
                          <span key={t.id} className="text-[10px] tracking-widest uppercase px-2 py-1 rounded bg-[#83B68B]/10 text-[#83B68B] border border-[#83B68B]/30" data-testid={`coach-team-${c.id}-${t.id}`}>
                            {t.name}{t.age_group ? ` · ${t.age_group}` : ""}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <Dialog open={!!editing} onOpenChange={(v)=>{ if(!v) setEditing(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Coach profile — {editing?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest opacity-70">Role type</Label>
                <Select value={profileForm.role_type || "none"} onValueChange={v=>setProfileForm({...profileForm, role_type: v === "none" ? "" : v})}>
                  <SelectTrigger data-testid="coach-role-type" className="mt-2"><SelectValue placeholder="Choose role" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— none —</SelectItem>
                    {ROLE_TYPES.map(rt => <SelectItem key={rt} value={rt}>{rt}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest opacity-70">Highest qualification</Label>
                <Input value={profileForm.highest_coaching_qualification} onChange={e=>setProfileForm({...profileForm, highest_coaching_qualification: e.target.value})} placeholder="FA Level 1" data-testid="coach-highest-qual" className="mt-2" />
              </div>
            </div>

            <div className="border border-border rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium">DBS check</div>
                <Switch data-testid="coach-dbs-held" checked={profileForm.dbs_held} onCheckedChange={(v)=>setProfileForm({...profileForm, dbs_held: v})} />
              </div>
              {profileForm.dbs_held && (
                <div>
                  <Label className="text-[10px] tracking-widest uppercase opacity-70">Expiry date</Label>
                  <Input type="date" value={profileForm.dbs_expiry} onChange={e=>setProfileForm({...profileForm, dbs_expiry: e.target.value})} data-testid="coach-dbs-expiry" className="mt-1.5" />
                </div>
              )}
            </div>

            <div className="border border-border rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium">Safeguarding Children</div>
                <Switch data-testid="coach-safeguarding-held" checked={profileForm.safeguarding_children_held} onCheckedChange={(v)=>setProfileForm({...profileForm, safeguarding_children_held: v})} />
              </div>
              {profileForm.safeguarding_children_held && (
                <div>
                  <Label className="text-[10px] tracking-widest uppercase opacity-70">Expiry date</Label>
                  <Input type="date" value={profileForm.safeguarding_children_expiry} onChange={e=>setProfileForm({...profileForm, safeguarding_children_expiry: e.target.value})} data-testid="coach-safeguarding-expiry" className="mt-1.5" />
                </div>
              )}
            </div>

            <div className="border border-border rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium">Intro to First Aid</div>
                <Switch data-testid="coach-first-aid-held" checked={profileForm.intro_first_aid_held} onCheckedChange={(v)=>setProfileForm({...profileForm, intro_first_aid_held: v})} />
              </div>
              {profileForm.intro_first_aid_held && (
                <div>
                  <Label className="text-[10px] tracking-widest uppercase opacity-70">Expiry date</Label>
                  <Input type="date" value={profileForm.intro_first_aid_expiry} onChange={e=>setProfileForm({...profileForm, intro_first_aid_expiry: e.target.value})} data-testid="coach-first-aid-expiry" className="mt-1.5" />
                </div>
              )}
            </div>

            <div className="flex items-center justify-between p-3 bg-muted rounded border border-border">
              <div className="pr-4">
                <div className="text-sm font-medium">Send compliance reminders</div>
                <div className="text-xs opacity-60 mt-0.5">Email this coach + admins when a cert hits 30 / 14 / 0 days to expiry. Requires the club-wide toggle to be on.</div>
              </div>
              <Switch data-testid="coach-reminders-enabled" checked={profileForm.compliance_reminders_enabled} onCheckedChange={(v)=>setProfileForm({...profileForm, compliance_reminders_enabled: v})} />
            </div>

            <button onClick={saveProfile} disabled={saving} data-testid="coach-profile-save" className="btn-volt w-full py-3 rounded-md disabled:opacity-50">
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!promoting} onOpenChange={(v)=>{ if(!v){ setPromoting(null); setPromoteResult(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Create account — {promoting?.name}</DialogTitle></DialogHeader>
          {promoteResult ? (
            <div className="space-y-3">
              <div className="text-sm">✅ Account created and {promoteResult.teams_linked} team{promoteResult.teams_linked === 1 ? "" : "s"} linked.</div>
              {promoteResult.temp_password && (
                <div className="border border-[#83B68B]/40 bg-[#83B68B]/10 rounded p-3 text-xs">
                  <div className="text-[10px] tracking-widest uppercase text-[#83B68B] mb-1">Temp password — share once</div>
                  <code className="font-mono text-sm break-all" data-testid="promote-temp-password">{promoteResult.temp_password}</code>
                </div>
              )}
              <div className="text-xs opacity-70">
                {promoteResult.welcome_email_sent ? "Welcome email sent." : "Welcome email was skipped or failed — share the login details manually."}
              </div>
              <button onClick={()=>{ setPromoting(null); setPromoteResult(null); }} className="btn-volt w-full py-3 rounded-md">Done</button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-xs opacity-70">This will create a coach account and re-link every team currently listing "{promoting?.name}" as coach.</div>
              <div>
                <Label className="text-xs uppercase tracking-widest opacity-70">Email <span className="text-[#83B68B]">(required)</span></Label>
                <Input type="email" value={promoteForm.email} onChange={e=>setPromoteForm({...promoteForm, email: e.target.value})} data-testid="promote-email" className="mt-2" placeholder="coach@example.com" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest opacity-70">Phone</Label>
                <Input value={promoteForm.phone} onChange={e=>setPromoteForm({...promoteForm, phone: e.target.value})} data-testid="promote-phone" className="mt-2" placeholder="Optional" />
              </div>
              <div className="flex items-center justify-between p-3 bg-muted rounded border border-border">
                <div className="text-sm">Send welcome email with temp password</div>
                <Switch data-testid="promote-welcome" checked={promoteForm.send_welcome_email} onCheckedChange={(v)=>setPromoteForm({...promoteForm, send_welcome_email: v})} />
              </div>
              <div className="flex items-center justify-between p-3 bg-muted rounded border border-border">
                <div className="text-sm">Force password change on first login</div>
                <Switch checked={promoteForm.force_password_change} onCheckedChange={(v)=>setPromoteForm({...promoteForm, force_password_change: v})} />
              </div>
              <button onClick={submitPromote} disabled={promoteBusy} data-testid="promote-submit" className="btn-volt w-full py-3 rounded-md disabled:opacity-50">
                {promoteBusy ? "Creating…" : "Create account & link teams"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ---- Bulk convert coach → user ---- */}
      <Dialog open={bulkOpen} onOpenChange={(v) => { setBulkOpen(v); if (!v) resetBulk(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-convert-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl inline-flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#83B68B]" /> Bulk create coach logins
            </DialogTitle>
            <DialogDescription className="text-xs opacity-70">
              Step 1 — download a CSV pre-filled with every coach who doesn't have a login yet. Step 2 — add each email (phone optional). Step 3 — upload it back. We'll validate first, then create only the good rows.
            </DialogDescription>
          </DialogHeader>

          {!bulkResult && (
            <div className="space-y-4">
              <div className="rounded-md border border-border p-4 bg-muted/30 text-sm space-y-2">
                <div className="text-[10px] tracking-widest uppercase opacity-70">CSV columns</div>
                <div className="text-xs opacity-80">
                  <code className="text-[#83B68B]">coach_name</code> · <code className="text-[#83B68B]">email</code> (required) · <code>phone</code> · <code>send_welcome_email</code> · <code>teams</code> (reference only, ignored on upload)
                </div>
                <button onClick={downloadBulkTemplate} data-testid="bulk-convert-template-btn" className="mt-2 text-xs text-[#83B68B] hover:underline inline-flex items-center gap-1">
                  <Download className="w-3.5 h-3.5" /> Download pre-filled template ({teamOnlyCount} coach{teamOnlyCount === 1 ? "" : "es"})
                </button>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest opacity-70">Choose your filled CSV</Label>
                <input
                  ref={bulkFileRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    setBulkFile(f || null);
                    if (f) previewBulk(f);
                  }}
                  data-testid="bulk-convert-file"
                  className="mt-2 block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-xs file:tracking-widest file:uppercase file:font-display file:font-bold file:bg-[#83B68B] file:text-white hover:file:bg-[#6EA579] file:cursor-pointer cursor-pointer"
                />
              </div>

              {bulkBusy && !bulkPreview && (
                <div className="text-center py-4 text-sm opacity-70">Reading your file…</div>
              )}

              {bulkPreview && (
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <div className="flex-1 rounded-md border border-[#83B68B]/40 bg-[#83B68B]/10 p-3">
                      <div className="text-[10px] tracking-widest uppercase text-[#83B68B]">Ready to create</div>
                      <div className="font-display font-black text-2xl mt-1">{bulkPreview.valid_count}</div>
                    </div>
                    <div className={`flex-1 rounded-md border p-3 ${bulkPreview.error_count ? "border-red-500/40 bg-red-500/10" : "border-border bg-muted/30"}`}>
                      <div className={`text-[10px] tracking-widest uppercase ${bulkPreview.error_count ? "text-red-400" : "opacity-60"}`}>Blocked</div>
                      <div className="font-display font-black text-2xl mt-1">{bulkPreview.error_count}</div>
                    </div>
                  </div>

                  {bulkPreview.errors.length > 0 && (
                    <div className="rounded-md border border-red-500/30 overflow-hidden">
                      <div className="bg-red-500/10 px-3 py-2 text-[10px] tracking-widest uppercase text-red-400 inline-flex items-center gap-2">
                        <AlertTriangle className="w-3.5 h-3.5" /> Rows we can't create
                      </div>
                      <div className="max-h-52 overflow-y-auto text-xs">
                        <table className="w-full">
                          <thead className="bg-muted/50 text-[10px] tracking-widest uppercase opacity-70">
                            <tr>
                              <th className="text-left px-3 py-1.5 w-12">Row</th>
                              <th className="text-left px-3 py-1.5">Coach</th>
                              <th className="text-left px-3 py-1.5">Email</th>
                              <th className="text-left px-3 py-1.5">Problem(s)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bulkPreview.errors.map((r, i) => (
                              <tr key={`${r.row_number}-${i}`} className="border-t border-border">
                                <td className="px-3 py-1.5 opacity-60">{r.row_number}</td>
                                <td className="px-3 py-1.5">{r.coach_name || "—"}</td>
                                <td className="px-3 py-1.5 font-mono">{r.email || "—"}</td>
                                <td className="px-3 py-1.5 text-red-400">{r.errors.join("; ")}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {bulkPreview.valid.length > 0 && (
                    <div className="rounded-md border border-border overflow-hidden">
                      <div className="bg-muted/30 px-3 py-2 text-[10px] tracking-widest uppercase opacity-70 inline-flex items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#83B68B]" /> Coaches we'll create logins for
                      </div>
                      <div className="max-h-52 overflow-y-auto text-xs">
                        <table className="w-full">
                          <thead className="bg-muted/50 text-[10px] tracking-widest uppercase opacity-70">
                            <tr>
                              <th className="text-left px-3 py-1.5">Coach</th>
                              <th className="text-left px-3 py-1.5">Email</th>
                              <th className="text-left px-3 py-1.5">Teams they'll be linked to</th>
                              <th className="text-left px-3 py-1.5">Email welcome</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bulkPreview.valid.map((r, i) => (
                              <tr key={r.email || r.row_number} className="border-t border-border">
                                <td className="px-3 py-1.5 font-medium">{r.coach_name}</td>
                                <td className="px-3 py-1.5 font-mono opacity-80">{r.email}</td>
                                <td className="px-3 py-1.5 opacity-70">{r.team_names.join(", ") || "—"}</td>
                                <td className="px-3 py-1.5">{r.send_welcome_email ? <CheckCircle2 className="w-3.5 h-3.5 text-[#83B68B]" /> : <X className="w-3.5 h-3.5 opacity-40" />}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 pt-1">
                    <button type="button" onClick={() => setBulkOpen(false)} className="flex-1 py-3 rounded-md border border-border text-xs tracking-widest uppercase font-display font-bold hover:border-white/40">Cancel</button>
                    <button
                      type="button"
                      onClick={commitBulk}
                      disabled={bulkBusy || bulkPreview.valid_count === 0}
                      data-testid="bulk-convert-commit-btn"
                      className="btn-volt flex-1 py-3 rounded-md disabled:opacity-40"
                    >
                      {bulkBusy ? "Creating…" : `Create ${bulkPreview.valid_count} login${bulkPreview.valid_count === 1 ? "" : "s"}`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {bulkResult && (
            <div className="space-y-4" data-testid="bulk-convert-result">
              <div className="rounded-md border border-[#83B68B]/40 bg-[#83B68B]/10 p-4">
                <div className="flex items-center gap-2 text-[#83B68B]">
                  <CheckCircle2 className="w-5 h-5" />
                  <div className="font-display font-black text-xl">
                    {bulkResult.created_count} login{bulkResult.created_count === 1 ? "" : "s"} created
                  </div>
                </div>
                {bulkResult.skipped_count > 0 && (
                  <div className="mt-2 text-xs opacity-80">
                    {bulkResult.skipped_count} row{bulkResult.skipped_count === 1 ? " was" : "s were"} skipped due to validation errors.
                  </div>
                )}
              </div>

              <div className="rounded-md border border-border overflow-hidden">
                <div className="bg-muted/50 px-3 py-2 text-[10px] tracking-widest uppercase opacity-70">Temporary passwords (share once each)</div>
                <div className="max-h-72 overflow-y-auto text-xs">
                  <table className="w-full">
                    <thead className="bg-muted/30 text-[10px] tracking-widest uppercase opacity-70">
                      <tr>
                        <th className="text-left px-3 py-1.5">Coach</th>
                        <th className="text-left px-3 py-1.5">Email</th>
                        <th className="text-left px-3 py-1.5">Teams linked</th>
                        <th className="text-left px-3 py-1.5">Temp password</th>
                        <th className="text-left px-3 py-1.5">Welcome</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkResult.created.map((c, i) => (
                        <tr key={c.id || c.email} className="border-t border-border">
                          <td className="px-3 py-1.5 font-medium">{c.name}</td>
                          <td className="px-3 py-1.5 font-mono opacity-80">{c.email}</td>
                          <td className="px-3 py-1.5 text-[#83B68B]">{c.teams_linked}</td>
                          <td className="px-3 py-1.5 font-mono select-all">{c.temp_password}</td>
                          <td className="px-3 py-1.5">{c.welcome_email_sent ? <CheckCircle2 className="w-3.5 h-3.5 text-[#83B68B]" /> : <X className="w-3.5 h-3.5 text-amber-400" />}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <button onClick={() => setBulkOpen(false)} className="btn-volt w-full py-3 rounded-md" data-testid="bulk-convert-done-btn">Done</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
